import {
  FloraVariants,
  forwardRef,
  HTMLFloraProps,
  styled,
  useIsMobile,
} from '@grupoboticario/flora-react'
import React, {
  Children,
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Slider, { LazyLoadTypes, Settings as SliderSettings } from 'react-slick'
import 'slick-carousel/slick/slick.css'
import { CustomButtom } from './aside-navigation/NavigationButton'
import { Dot, DotProps } from './bottom-navigation/NavigationDot'
import { CarouselContextState, CarouselContextType, DEFAULT_CONTEXT_VALUE } from './CarouselContext'

const StyledCarouselContext = styled('div', {
  position: 'relative',

  [`${CustomButtom?.toString()}`]: {
    transform: 'translate(0, -93%)',

    '@mobile': {
      transform: 'translate(0, -75%)',
    },
  },

  variants: {
    variant: {
      container: {
        padding: '0 calc($6 + $2)',

        '@mobile': {
          padding: '0 calc($10 + $2)',
        },

        [`${CustomButtom?.toString()}`]: {
          '&[data-to=previous]': {
            left: 0,
          },

          '&[data-to=next]': {
            right: 0,
          },
        },
      },
      fluid: {
        [`${CustomButtom?.toString()}`]: {
          '&[data-to=previous]': {
            left: '$2',
          },

          '&[data-to=next]': {
            right: '$2',
          },
        },
      },
    },
  },

  defaultVariants: {
    variant: 'container',
  },
})

export const CarouselContext = createContext<CarouselContextType>(DEFAULT_CONTEXT_VALUE)

export type CarouselProps = FloraVariants<typeof StyledCarouselContext> &
  HTMLFloraProps<'div'> &
  Partial<
    Pick<CarouselContextState, 'onDotPress' | 'onNextPress' | 'onPreviousPress' | 'onSlideChange'>
  > &
  Pick<DotProps, 'color'>

export const Carousel: FC<PropsWithChildren<CarouselProps>> = ({
  onDotPress,
  onNextPress,
  onPreviousPress,
  onSlideChange,
  color = 'standard',
  children,
  ...props
}) => {
  const [state, setState] = useState(DEFAULT_CONTEXT_VALUE.state)

  const handleOnDotPress = useCallback(
    (index: number) => {
      onDotPress?.(index)
    },
    [onDotPress],
  )

  const handleOnNextPress = useCallback(() => {
    onNextPress?.()
  }, [onNextPress])

  const handleOnPreviousPress = useCallback(() => {
    onPreviousPress?.()
  }, [onPreviousPress])

  const handleOnSlideChange = useCallback(
    (index: number) => {
      onSlideChange?.(index)
    },
    [onSlideChange],
  )

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      onDotPress: handleOnDotPress,
      onNextPress: handleOnNextPress,
      onPreviousPress: handleOnPreviousPress,
      onSlideChange: handleOnSlideChange,
      color,
    }))
  }, [handleOnDotPress, handleOnNextPress, handleOnPreviousPress, handleOnSlideChange, color])

  return (
    <CarouselContext.Provider
      value={{
        state,
        setState,
      }}
    >
      <StyledCarouselContext {...props}>{children}</StyledCarouselContext>
    </CarouselContext.Provider>
  )
}

// ------------------------------------ //

const MAX_SLIDES_TO_SHOW_DESKTOP = 6
const MAX_SLIDES_TO_SHOW_MOBILE = 4

const StyledCarouselContent = styled('div', {
  width: '100%',

  '.slick-list': {
    minHeight: '$14',
  },
})

const StyledList = styled('ul', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '$4',
})

export type CarouselContentProps = {
  infinite?: boolean
  slidesToShow?: number
  autoPlay?: boolean
  autoPlaySpeed?: number
  transitionSpeed?: number
  fade?: boolean
  pauseOnInteraction?: boolean
  lazyLoad?: LazyLoadTypes
}

export const CarouselContent = forwardRef<
  typeof StyledCarouselContent,
  PropsWithChildren<CarouselContentProps>
>(
  (
    {
      infinite = true,
      slidesToShow: slidesToShowProps = 1,
      autoPlay = false,
      pauseOnInteraction = true,
      autoPlaySpeed = 5000,
      fade = false,
      transitionSpeed = 250,
      children,
      lazyLoad,
      ...props
    },
    forwardedRef,
  ) => {
    const {
      setState: setContext,
      state: { onSlideChange: onSlideChangeContext, color },
    } = useContext(CarouselContext)
    const [currentIndex, setCurrentIndex] = useState(0)
    const sliderRef = useRef<Slider | null>(null)
    const isMobile = useIsMobile()

    const onSlideChange = (index: number) => {
      const currentIndexValue = Math.ceil(index / slidesToShow)
      onSlideChangeContext(currentIndexValue)
      setCurrentIndex(currentIndexValue)
      setContext((prevContext) => ({
        ...prevContext,
        currentIndex: currentIndexValue,
      }))
    }

    const slidesToShow = useMemo(() => {
      if (slidesToShowProps > MAX_SLIDES_TO_SHOW_MOBILE && isMobile) {
        return MAX_SLIDES_TO_SHOW_MOBILE
      }

      return slidesToShowProps > MAX_SLIDES_TO_SHOW_DESKTOP
        ? MAX_SLIDES_TO_SHOW_DESKTOP
        : slidesToShowProps
    }, [isMobile, slidesToShowProps])

    const sliderSettings: SliderSettings = {
      accessibility: true,
      afterChange: onSlideChange,
      arrows: true,
      autoplay: autoPlay,
      autoplaySpeed: autoPlaySpeed,
      dots: true,
      infinite,
      lazyLoad,
      nextArrow: <div />,
      pauseOnDotsHover: pauseOnInteraction,
      pauseOnFocus: pauseOnInteraction,
      pauseOnHover: pauseOnInteraction,
      prevArrow: <div />,
      appendDots: (dots) => {
        return (
          <StyledList>
            <>{dots}</>
          </StyledList>
        )
      },
      customPaging: (index: number) => {
        return (
          <Dot
            index={index}
            aria-label={`Exibir Slide ${index + 1}`}
            active={currentIndex === index}
            color={color}
          />
        )
      },
      slidesToShow,
      slidesToScroll: slidesToShow,
      fade,
      swipe: true,
      speed: transitionSpeed,
    }

    useEffect(() => {
      if (sliderRef && sliderRef.current) {
        const { current: sliderCurrentRef } = sliderRef
        setContext((prevContext) => ({
          ...prevContext,
          goTo: (index: number) => {
            sliderCurrentRef.slickGoTo(index)
          },
          goToNext: () => {
            sliderCurrentRef.slickNext()
          },
          goToPrevious: () => {
            sliderCurrentRef.slickPrev()
          },
        }))
      }
    }, [sliderRef, setContext, currentIndex, slidesToShow])

    useEffect(() => {
      setContext((prevContext) => ({
        ...prevContext,
        pages: Math.ceil(Children.count(children) / slidesToShow),
        infinite: infinite,
      }))
    }, [children, setContext, slidesToShow, infinite])

    return (
      <StyledCarouselContent {...props} ref={forwardedRef}>
        <Slider ref={sliderRef} {...sliderSettings}>
          {children}
        </Slider>
      </StyledCarouselContent>
    )
  },
)
